import { DatePipe } from '@angular/common';
import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  Input,
} from '@angular/core';
import { DprForecastActivityReport } from '@report/models';
import { StaticMessage } from '@shared/enums';
import { DataType, ProjectSetup, SelectionItem } from '@shared/models';
import { DprForecastActivity } from '@shared/models/dpr';
import {
  buildBooleanValues,
  compareTime,
  convertTimeToDate,
  getValue,
} from '@shared/utils';
import cloneDeep from 'lodash.clonedeep';

@Component({
  selector: 'app-report-forecast-activities',
  templateUrl: './report-forecast-activities.component.html',
  styleUrls: ['./report-forecast-activities.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReportForecastActivitiesComponent implements OnInit {
  @Input({ required: true }) dprForecastActivities: DprForecastActivity[];
  @Input({ required: true }) projectSetup: ProjectSetup;

  forecastActivityReport: DprForecastActivityReport[] = [];

  StaticMessage = StaticMessage;

  constructor(private datePipe: DatePipe) {}

  ngOnInit(): void {
    // sort by date, then start, then finish
    const sortedForecastActivities: DprForecastActivity[] = cloneDeep(
      this.dprForecastActivities
    ).sort(
      (a, b) =>
        new Date(a.date).getTime() - new Date(b.date).getTime() ||
        compareTime(convertTimeToDate(a.start), convertTimeToDate(b.start)) ||
        compareTime(convertTimeToDate(a.finish), convertTimeToDate(b.finish))
    );

    sortedForecastActivities.forEach((forecastActivity) => {
      const toAdd: DprForecastActivityReport = {
        activityGroupName: (this.projectSetup.activityGroupList ?? []).find(
          (x) => x.id === forecastActivity.activityGroupId
        ).name,
        activityName: (this.projectSetup.activityList ?? []).find(
          (x) => x.id === forecastActivity.activityId
        ).name,
        locationName: (this.projectSetup.locationList ?? []).find(
          (x) => x.id === forecastActivity.locationId
        ).name,
        forecastCategoryName: (this.projectSetup.forecastCategories ?? []).find(
          (x) => x.id === forecastActivity.forecastCategoryId
        ).name,
        date: forecastActivity.date,
        start: forecastActivity.start,
        finish: forecastActivity.finish,
        comment: forecastActivity.comment || '-',
        customDataReport: [],
        subActivitiesReport: [],
      };

      // Custom data

      const activityMobilisationCustomDataIds: number[] =
        this.projectSetup.activityMobilisationList
          .filter((am) => am.activityId === forecastActivity.activityId)
          .map((am) => am.customDataId);

      forecastActivity.dprForecastActivityCustomDataList.forEach((cd) => {
        if (cd && activityMobilisationCustomDataIds.includes(cd.customDataId)) {
          const customData = (this.projectSetup.customDataList ?? []).find(
            (x) => x.id === cd.customDataId
          );

          if (!!cd.value?.toString()) {
            let listOptions: SelectionItem[];

            switch (customData.dataTypeId) {
              case DataType.List:
                listOptions = cloneDeep(
                  this.projectSetup.customDataValueList || []
                ).map((x) => ({ id: x.id, name: x.value }));
                break;
              case DataType.Boolean:
                listOptions = buildBooleanValues(
                  customData.booleanTerminologyId
                ).map((x) => ({ id: x.id, name: x.value }));
                break;
              default:
                listOptions = [];
            }

            const value = getValue(
              cd.value,
              customData.dataTypeId,
              listOptions,
              this.datePipe,
              customData.decimalCount
            );

            toAdd.customDataReport.push({
              name: customData.name,
              comment: cd.comment ?? '-',
              value,
            });
          }
        }
      });

      // Subactivities

      forecastActivity.dprForecastSubActivities.forEach((subActivity) => {
        toAdd.subActivitiesReport.push({
          name: subActivity.name,
          start: subActivity.start,
          finish: subActivity.finish,
        });
      });

      this.forecastActivityReport.push(toAdd);
    });
  }
}
